import React, { useState, useContext, useEffect } from "react";
import * as Icons from "Components/Icons";
import { Button, SpinningButton, Modal, ModalHeader, ModalBody, ModalFooter } from "Components/ReactstrapSlim";
import { LayoutContext } from "Components/LayoutContext";
import UserContext from "features/EnterprisePortal/UserContext";
import useMediaBreakpointDown from "Hooks/useMediaBreakpointDown";
import { ITeamMemberReservation, ReservationStatus, ReservationGuestsClient, ReservationVisitStatesClient, IReservationMaybeMemberViewModel } from "Services/API/API";
import { useApiClient } from "Services/API/useApiClient";
import InviteGuestsModal from "../InviteGuestsModal";
import CancelReservationModal from "../CancelReservationModal";
import { GuestAvatarList } from "../../GuestAvatarList";
import classes from "./styles.module.scss"
import { VenueCalendarModal } from "Components/VenueCalendarModal";
import { formatReservationTime } from "Utils/datetime";
import { formatStringDate } from "Utils/date";
import { BIN } from "features/ComponentsLib";

export interface ReservationDetailsModalInternalProps {
   isOpen: boolean,
   toggle: () => void,
   reservation: ITeamMemberReservation,
   container?: string | HTMLElement;

   onUpdateReservation: (reservationId?: Guid, occurrenceDate?: string) => void;
   onUpdateReservationList?: (reservationId?: Guid, occurrenceDate?: string) => void
}
export const ReservationDetailsModalInternal: React.FC<ReservationDetailsModalInternalProps> = ({ isOpen, toggle, reservation, container, onUpdateReservation, onUpdateReservationList }) => {
   const layoutContext = useContext(LayoutContext);
   const { user } = useContext(UserContext);

   const [fade, setFade] = useState(true);
   const [isOpenInternal, setIsOpenInternal] = useState(true);
   const [isPreBinOpen, setIsPreBinOpen] = useState(false);
   const [isInviteGuestsModalOpen, setIsInviteGuestsModalOpen] = useState(false);
   const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
   const [isCheckingLoading, setIsCheckingLoading] = useState(false);
   const [guests, setGuests] = useState<IReservationMaybeMemberViewModel[]>(reservation.invitees);
   const [entryInstructionsExpanded, setEntryInstructionsExpanded] = useState(false);
   const reservationVisitStatesClient = useApiClient(ReservationVisitStatesClient);
   const reservationGuestsClient = useApiClient(ReservationGuestsClient);
   const isMobileScreen = useMediaBreakpointDown("sm");
   const [modalRef, setModalRef] = useState<Element | null>(null);

   const openModal = (callback: () => void) => {
      setIsOpenInternal(false);
      callback();
   }

   const messageHost = () => {
      openModal(() => {
         import("LiquidSpaceEmbedded").then(x => x.default.actions["ls-messageHost"]({
            venueId: reservation.venueId, workspaceId: reservation.workspaceId, onClose: () => {
               setIsOpenInternal(true);
            }
         }))
      });
   }

   const cancelReservation = () => {
      openModal(() => {
         setIsCancelModalOpen(true);
      });
   }

   const editReservation = () => {
      openModal(() => {
         BIN({
            type: "EDIT",
            reservationId: reservation.idForLink,
            occurenceDate: reservation.occurrenceDate,
            onClose: (isUpdated) => {
               setIsOpenInternal(true);
               if (isUpdated)
                  onUpdateReservation(reservation.idForLink, reservation.occurrenceDate);
            }
         });
      })
   }

   const addGuests = () => {
      openModal(() => {
         setIsInviteGuestsModalOpen(true);
      })
   }

   const checkIn = async () => {
      try {
         setIsCheckingLoading(true);
         const result = await reservationVisitStatesClient.checkIn(reservation.idForLink, reservation.occurrenceDate);
         if (result.success) {
            await onUpdateReservation(reservation.idForLink, reservation.occurrenceDate);
         }
      }
      catch (err) {
         layoutContext.showError(err);
      }
      finally {
         setIsCheckingLoading(false);
      }
   }

   const checkOut = async () => {
      try {
         setIsCheckingLoading(true);
         const result = await reservationVisitStatesClient.checkOut(reservation.idForLink, reservation.occurrenceDate);
         if (result.success) {
            if (onUpdateReservationList)
               await onUpdateReservationList(reservation.idForLink, reservation.occurrenceDate);
            else
               await onUpdateReservation(reservation.idForLink, reservation.occurrenceDate);
         }
      }
      catch (err) {
         layoutContext.showError(err);
      }
      finally {
         setIsCheckingLoading(false);
      }
   }

   const loadGuests = async (abortSignal: AbortSignal) => {
      if (reservation.invitees?.length) {
         setGuests(await reservationGuestsClient.get(reservation.idForLink, abortSignal));
      }
      else {
         setGuests([]);
      }
   }

   const openPreBin = () => {
      openModal(() => {
         setIsPreBinOpen(true);
      });
   }

   const closePreBin = () => {
      setIsPreBinOpen(false);
   }

   //Fade only on first open. When swtiching between other modals (Messenger, Edit, Cancel) - do not fade
   useEffect(() => {
      setTimeout(() => {
         setFade(false);
      }, 1500);
   }, [])

   useEffect(() => {
      if (!isOpenInternal && !isCancelModalOpen && !isInviteGuestsModalOpen && !isPreBinOpen)
         setIsOpenInternal(true);
   }, [isCancelModalOpen, isInviteGuestsModalOpen, isPreBinOpen])

   useEffect(() => {
      const abortController = new AbortController();
      loadGuests(abortController.signal)

      return function cleanup() {
         abortController.abort();
      }
   }, [reservation.invitees]);

   return (<>
      <Modal isOpen={isOpenInternal && isOpen} container={container} toggle={toggle} className={classes.modal} centered fade={fade} innerRef={setModalRef}>
         <ModalHeader toggle={toggle} compact>
            Reservation Details
         </ModalHeader>
         <ModalBody withoutLeftRightPaddings>
            <div className={classes.gallery}>
               <div className={classes.galleryImage} style={{ backgroundImage: `url(${reservation.picture})` }}></div>
            </div>
            <div className={classes.modalBodyInfo}>
               <div className={classes.bodyTitle}>
                  <a className={classes.spaceName} href={reservation.workspaceUrl} target="_blank">{reservation.workspaceName}</a>
                  <a className={classes.spaceVenueName} href={reservation.venueUrl} target="_blank">{reservation.venueName}</a>
               </div>

               <div className={classes.bodyDetails}>
                  <div className={classes.hostDetails}>
                     <div className={classes.hostDetailsInfo}>
                        <div className={classes.detailIcon}><Icons.User /></div>
                        <div className={classes.detailInfo}>
                           <div className={classes.detailInfoLine}>{reservation.venueHostName} {reservation.venueHostLastName} (Host)</div>
                           <div>{reservation.venueHostPhone}</div>
                        </div>
                     </div>
                     <div><GuestAvatarList organizer={reservation.organizer} guests={guests} capacity={reservation.workspaceCapacity} displayCount={isMobileScreen ? 1 : 3} extendOnClick bindTarget={modalRef || undefined} onAddGuests={reservation.reservationOperations?.inviteAllowed ? addGuests : undefined} /></div>
                  </div>

                  <div className={classes.reservationDetailsInfo}>
                     <div className={classes.detailIcon}><Icons.Calendar /></div>
                     <div className={classes.detailInfo}>
                        <div className={classes.reservationTimeRange}>{formatStringDate(reservation.startTime, "ddd, MMM D, YYYY")} {formatReservationTime(reservation.startTime, reservation.endTime)} {reservation.occurrenceDate ? <Icons.Loop width={16} height={16} /> : ""}</div>
                        <div>{reservation.spaceTypeForCapacity}</div>
                     </div>
                  </div>

                  <div className={classes.locationDetailsInfo}>
                     <div className={classes.detailIcon}><Icons.LocationOn /></div>
                     <div className={classes.detailInfo}>
                        <div className={classes.detailInfoLine}>{reservation.address}</div>
                        {entryInstructionsExpanded && <div className={classes.entryInstructions}>
                           <div className={classes.entryInstructionsCaption}>Entry Instructions</div>
                           <div className={classes.entryInstructionsText} dangerouslySetInnerHTML={{ __html: reservation.entryInformation }} />
                           <div>
                              <a href="#" onClick={(e) => { e.preventDefault(); setEntryInstructionsExpanded(false); }}>Hide</a>
                           </div>
                        </div>}
                        {!!reservation.entryInformation && !entryInstructionsExpanded && <div className={classes.detailAction}>
                           <a href="#" onClick={(e) => { e.preventDefault(); setEntryInstructionsExpanded(true); }}>Entry Instructions</a>
                        </div>}
                     </div>
                  </div>
                  {reservation.amenities.length > 0 && <div className={classes.amenitiesDetailsInfo}>
                     <div className={classes.detailIcon}><Icons.License /></div>
                     <div className={classes.detailInfo}>{reservation.amenities.map(a => a.name).join(", ")}</div>
                  </div>}
                  {!!reservation.bookingReason && <div className={classes.bookingReasonDetailsInfo}>
                     <div className={classes.detailIcon}><Icons.BookingPurpose /></div>
                     <div className={classes.detailInfo}>Reason: {reservation.bookingReason}</div>
                  </div>}
                  {reservation.reservationOperations?.cancelAllowed && <div className={classes.cancelCctaMobileWrapper}>
                     <a href="#" onClick={(e) => { e.preventDefault(); cancelReservation(); }}>Cancel reservation</a>
                  </div>}
               </div>
            </div>
         </ModalBody>
         <ModalFooter className={classes.footer} compact>
            <a href="#" className={classes.linkCta} onClick={(e) => { e.preventDefault(); messageHost(); }}>Message Host</a>
            {
               (reservation.reservationOperations?.editAllowed || reservation.reservationOperations?.checkInAllowed || reservation.reservationOperations?.checkOutAllowed) && <>
                  {/* {!isMobileScreen && reservation.reservationOperations.cancelAllowed && <a href="#" onClick={(e) => { e.preventDefault(); cancelReservation(); }} className={classes.linkCta}>Cancel reservation</a>} */}
                  <Button size="sm" color="primary" onClick={openPreBin} fluid={isMobileScreen}>{user.id == reservation.ownerId ? "Book Again" : "Work Here"}</Button>
                  {reservation.reservationOperations.editAllowed && <Button size="sm" color="secondary" onClick={editReservation} fluid={isMobileScreen}>Edit</Button>}
                  {(reservation.status == ReservationStatus.Future || reservation.reservationOperations.checkInAllowed) && !reservation.reservationOperations.checkOutAllowed && <SpinningButton isLoading={isCheckingLoading} size="sm" color="primary" disabled={!reservation.reservationOperations.checkInAllowed} onClick={checkIn} fluid={isMobileScreen}>Check in</SpinningButton>}
                  {reservation.reservationOperations.checkOutAllowed && <SpinningButton isLoading={isCheckingLoading} size="sm" color="primary" onClick={checkOut} fluid={isMobileScreen}>Check out</SpinningButton>}
               </>
            }
         </ModalFooter>
      </Modal>
      {isInviteGuestsModalOpen && <InviteGuestsModal reservation={reservation} container={container} onClosePopup={() => setIsInviteGuestsModalOpen(false)} onSendInvite={async () => await onUpdateReservation(reservation.idForLink, reservation.occurrenceDate)} />}
      {isCancelModalOpen && <CancelReservationModal reservation={reservation} container={container} onClose={() => setIsCancelModalOpen(false)} onSingleSuccess={async () => { toggle(); await onUpdateReservation(reservation.idForLink, reservation.occurrenceDate) }} onRecurrentSuccess={async () => { toggle(); await onUpdateReservationList?.(reservation.idForLink, reservation.occurrenceDate) }} />}
      {isPreBinOpen && <VenueCalendarModal isOpen={true} container={container} toggle={closePreBin} venueId={reservation.venueId} />}
   </>);
}